import React from 'react';
import { observer } from 'mobx-react-lite';
import { IMInputText, OverlaySpinner } from '@insurancemenu/imenu-components-lib';
import { useSelfRegistrationPageStore } from 'store/pages/self-registration/pageStore';
import SupportBlock from 'components/common/SupportBlock';
import SubtitleWithLines from 'components/common/SubtitleWithLines';
import {
  IMFormStyled,
  TitleStyled,
  GuidelinesStyled,
  InvitedEeGuidelineStyled,
  IMSubmitButtonStyled,
  IMFormFooterStyled,
} from './styles';

interface Props {
  scrollUp: () => void;
}

const SelfRegistrationForm: React.FC<Props> = observer(({ scrollUp }) => {
  const { isUserInvited, formController, isLoading, inviteEmployeeToGroup } = useSelfRegistrationPageStore();

  // The current IM Form implementation doesn't prevent the default form submission behavior.
  // In this case, I also need to scroll the window to the top to reveal the updated view.
  const onFormSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    await inviteEmployeeToGroup();
    scrollUp();
  };

  return (
    <>
      <TitleStyled>Start Self Registration</TitleStyled>
      <SubtitleWithLines text="Please fill out the form below to get started" />

      {isUserInvited ? (
        <InvitedEeGuidelineStyled>
          The link to complete your registration has been sent.
          <br /> Please check your email.
        </InvitedEeGuidelineStyled>
      ) : (
        <GuidelinesStyled>
          We only collect the minimum information needed to send your invitation. You&apos;ll <b>receive an email </b>{' '}
          with a link to complete your registration.
        </GuidelinesStyled>
      )}

      <IMFormStyled formController={formController} id="self-registration-form" onSubmit={onFormSubmit}>
        <IMInputText name="firstName" label="First Name" size="full" />
        <IMInputText name="lastName" label="Last Name" size="full" />
        <IMInputText name="email" label="Email" size="full" />
        <IMInputText name="dob" label="Date of Birth" size="full" htmlType="date" />
        <IMFormFooterStyled>
          <IMSubmitButtonStyled disabled={isLoading}>Submit</IMSubmitButtonStyled>
        </IMFormFooterStyled>
      </IMFormStyled>
      <SubtitleWithLines text="or" narrow />
      <SupportBlock type="WITH_IM_ACCOUNT" />
      <OverlaySpinner display={isLoading} data-test-id="self-registration-form-spinner-test-id" />
    </>
  );
});

export default SelfRegistrationForm;
